import React from 'react';
import { Box, Typography } from '@mui/material';
import SearchIcon from '../../../CustomSVGIcons/SearchIcon';
import { ICampaignSales } from '../../../../redux/contest/types';
import { BrandContestSalesCard } from './BrandContestSalesCard';
import {
    brandAwardEmptyStateSx,
    brandAwardBodyTextSx,
    midnight,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import {
    brandSalesDesktopHeaderVisibleSx,
    brandSalesGridColumns,
    brandSalesStackedHeaderSx,
} from './brandSalesCardLayout';

const EMPTY_MESSAGE = 'No Data Available';

const HEADER_CELLS: { lines: string[]; align: 'start' | 'center' }[] = [
    { lines: ['User'], align: 'start' },
    { lines: ['Amount'], align: 'center' },
    { lines: ['Currency'], align: 'center' },
    { lines: ['Discount'], align: 'center' },
    { lines: ['Status'], align: 'center' },
];

const StackedHeaderLabel: React.FC<{ lines: string[]; align: 'start' | 'center' }> = ({ lines, align }) => (
    <Box sx={brandSalesStackedHeaderSx(align)}>
        {lines.map(line => (
            <Typography key={line} component="span" sx={{ fontSize: 'inherit', fontFamily: 'inherit' }}>
                {line}
            </Typography>
        ))}
    </Box>
);

export interface BrandContestSalesListProps {
    sales: ICampaignSales[];
    formatDateDay: (iso: string) => string;
    formatDateTime: (iso: string) => string;
}

const BrandContestSalesEmptyCard: React.FC = () => (
    <Box
        sx={{
            ...brandAwardEmptyStateSx,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            minHeight: 120,
        }}>
        <SearchIcon fill={midnight.onSurfaceVariant} />
        <Typography sx={{ ...brandAwardBodyTextSx, fontSize: 16 }}>{EMPTY_MESSAGE}</Typography>
    </Box>
);

const BrandContestSalesList: React.FC<BrandContestSalesListProps> = ({
    sales,
    formatDateDay,
    formatDateTime,
}) => {
    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    ...brandSalesDesktopHeaderVisibleSx,
                    gridTemplateColumns: brandSalesGridColumns.lg,
                    columnGap: 2,
                    alignItems: 'end',
                    px: 3,
                    py: 1,
                    mb: 0.5,
                }}>
                {HEADER_CELLS.map(({ lines, align }) => (
                    <StackedHeaderLabel key={lines.join('-')} lines={lines} align={align} />
                ))}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, lg: 2 } }}>
                {sales.length === 0 ? (
                    <BrandContestSalesEmptyCard />
                ) : (
                    sales.map(sale => (
                        <BrandContestSalesCard
                            key={sale._id}
                            sale={sale}
                            dateDay={formatDateDay(sale.createdAt)}
                            dateTime={formatDateTime(sale.createdAt)}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
};

export { BrandContestSalesList, EMPTY_MESSAGE as BRAND_SALES_EMPTY_MESSAGE };
